// src/lib/analytics.ts
import posthog from "posthog-js";
import { isPublicAnalyticsEnabled } from "@/lib/feature-flags";

let initialized = false;

const INITIAL_UTM_FLAG = "ld_initial_utm_set";
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

export function initPostHog(): void {
  if (initialized) return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    persistence: "localStorage",
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") ph.debug();
    },
  });

  initialized = true;
}

/**
 * Captura un evento genérico. Punto de extensión para los eventos de dominio
 * (registro de ligas, creación de equipos, etc.) cuando existan.
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

// --- Eventos públicos ---
// Gated por `isPublicAnalyticsEnabled` (NEXT_PUBLIC_ENABLE_PUBLIC_ANALYTICS).

export function trackPageview(url: string): void {
  if (!isPublicAnalyticsEnabled) return;
  capture("$pageview", { $current_url: url });
}

// --- First-touch UTM attribution ---

// Persiste utm_* + referrer del primer pageview de la sesión como propiedades
// initial_* del perfil vía $set_once. Sobrevive a sucesivos pageviews y al
// identify() del registro porque PostHog mergea propiedades anónimas al perfil
// identificado.
export function captureInitialUtm(searchParams: URLSearchParams): void {
  if (!isPublicAnalyticsEnabled) return;
  if (!initialized) return;
  if (typeof window === "undefined") return;
  if (window.sessionStorage.getItem(INITIAL_UTM_FLAG)) return;

  const setOnceProps: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = searchParams.get(key);
    if (value) setOnceProps[`initial_${key}`] = value;
  }
  if (typeof document !== "undefined" && document.referrer) {
    setOnceProps.initial_referrer = document.referrer;
  }

  window.sessionStorage.setItem(INITIAL_UTM_FLAG, "1");

  if (Object.keys(setOnceProps).length > 0) {
    posthog.setPersonProperties(undefined, setOnceProps);
  }
}

// --- Identificación ---

export function identifyUser(
  userId: string,
  traits: { email: string; name: string }
): void {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

export function resetUser(): void {
  if (!initialized) return;
  posthog.reset();
}
